import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/utils';

/**
 * The tinted square an icon sits in on a status surface.
 *
 * **Tokens, never raw palette.** The surfaces this replaces ran four different
 * colour systems for the same chip — `bg-green-100 dark:bg-green-900/20`,
 * `bg-blue-100 dark:bg-muted`, `bg-red-100`, `bg-amber-50` — several of them a
 * raw palette in light and tokens in dark on the same element.
 *
 * **`info` maps to `--info`, NOT `--primary`.** In light, `--primary` is
 * near-black because it is the action *fill* for buttons rather than an
 * informational hue; using it here rendered in-progress medallions black while
 * every sibling was coloured.
 *
 * Measured against the composited card: 3.67–4.15:1 light, 6.98–8.99:1 dark —
 * all clear of the 3:1 non-text threshold.
 *
 * The icon is passed as `children` rather than as a component prop, because the
 * two call sites need different things inside: one renders a Lucide glyph, the
 * other a spinner. Size the glyph at the call site (`h-4 w-4` under `sm`,
 * `h-5 w-5` under `md`).
 *
 * `aria-hidden` is baked in. This is decoration in every known use — the
 * meaning is carried by the heading beside it, and announcing the container
 * gave screen readers an empty group.
 */
const medallionVariants = cva('flex shrink-0 items-center justify-center', {
  variants: {
    tone: {
      info: 'bg-info/10 text-info',
      success: 'bg-success/15 text-success-emphasis',
      error: 'bg-destructive/10 text-destructive',
      warning: 'bg-warning/15 text-warning-emphasis',
      brand: 'bg-brand-50 text-brand-500 dark:tint-brand-strong',
      /** No tint — for surfaces that carry their own tone on the container. */
      neutral: 'bg-muted text-muted-foreground',
    },
    size: {
      /** In-card, beside a line of copy. */
      sm: 'h-8 w-8 rounded-lg',
      /** The heading medallion on a status screen. */
      md: 'h-11 w-11 rounded-xl',
    },
  },
  defaultVariants: { tone: 'info', size: 'md' },
});

export interface IconMedallionProps
  extends VariantProps<typeof medallionVariants> {
  children: React.ReactNode;
  className?: string;
}

export function IconMedallion({
  tone,
  size,
  className,
  children,
}: IconMedallionProps) {
  return (
    <div
      className={cn(medallionVariants({ tone, size }), className)}
      aria-hidden
    >
      {children}
    </div>
  );
}
